import {
  useHistoryDictionaries,
  useHistoryPageFilters,
  useOpenStatementModalFromQuery,
} from '../model';
import { useOperationsHistory } from '@/shared/api';
import { buildOperationsGetQueryParams, filterOperations } from '../lib';
import {
  HistoryOperationsList,
  HistoryOperationsListSkeleton,
  EmptyStateView,
} from './operations-list';
import { useCallback, useMemo } from 'react';
import type { OperationDto, OperationFilter } from '@/shared/types';
import { ScrollTopButton } from '@/shared/components';
import { useAppSettingsStore, useScrollDetection } from '@/shared/lib';
import {
  useOperationHistoryDetailsModal,
  useOperationsRefetch,
} from '@/entities/operation';
import { useGenerateStatementModal } from '@/features/generate-statement';
import {
  HistoryExportButton,
  HistoryFilterSkeleton,
  HistoryOperationTypeSelect,
  HistoryDateFilterSelect,
} from './list-controls';
import { useVerificationInfoModal } from '@/entities/verification';
import { useSessionStore } from '@/entities/session';
import { useAccountsRefetch } from '@/entities/account';

const HistoryPage = () => {
  const { getLanguage } = useAppSettingsStore();
  const { session } = useSessionStore();

  const isVerified = session?.additional?.isVerified || false;
  const language = getLanguage();

  const { openOperationDetailsModal } = useOperationHistoryDetailsModal();
  const { openGenerateStatementModal } = useGenerateStatementModal();
  const { openVerificationInfoModal } = useVerificationInfoModal();

  useOpenStatementModalFromQuery();

  const { refetchAll: refetchAccounts } = useAccountsRefetch({
    userId: session?.userId,
  });
  const { refetchAll: refetchOperations } = useOperationsRefetch({
    userId: session?.userId,
  });

  const {
    isLoading: isOperationsDictionariesLoading,
    error: operationsDictionariesError,
    operationTypes,
    dateFilters,
    operationTypeIcons,
    operationStatusIcons,
  } = useHistoryDictionaries();

  const { filters, setPageFilters, resetPeriod, isPeriodApplied } =
    useHistoryPageFilters({ operationTypes });

  const { ref: containerRef, hasScrolled } = useScrollDetection({
    threshold: 300,
    debounceMs: 100,
  });

  const queryParams = useMemo(
    () => buildOperationsGetQueryParams(filters),
    [filters]
  );

  const {
    operationsHistory,
    isOperationsHistoryLoading,
    operationsHistoryError,
  } = useOperationsHistory('all', queryParams);

  const filteredOperationsHistory = useMemo(() => {
    if (!operationsHistory) return undefined;

    return {
      ...operationsHistory,
      operations: filterOperations(operationsHistory.operations, filters),
    };
  }, [operationsHistory, filters]);

  const error = operationsDictionariesError || operationsHistoryError;

  const handleStatusChange = useCallback(() => {
    refetchAccounts();
    refetchOperations();
  }, [refetchAccounts, refetchOperations]);

  const handleOperationItemClick = useCallback(
    (operation: OperationDto) => {
      openOperationDetailsModal({
        operation,
        onStatusChange: handleStatusChange,
      });
    },
    [openOperationDetailsModal]
  );

  const isOperationsListEmpty = !filteredOperationsHistory?.operations?.length;

  const scrollToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectOperationType = useCallback(
    (value: OperationFilter) => {
      setPageFilters({ ...filters, filter: value });
    },
    [filters, setPageFilters]
  );

  const handleHistoryExportClick = useCallback(() => {
    if (!isVerified) {
      openVerificationInfoModal();
      return;
    }

    openGenerateStatementModal({
      dateFilter: filters?.datefilter,
      dateFrom: filters?.datefrom,
      dateTo: filters?.dateto,
    });
  }, [
    openGenerateStatementModal,
    filters,
    isVerified,
    openVerificationInfoModal,
  ]);

  if (error) {
    return <div>Error: {error.message || String(error)}</div>;
  }

  return (
    <div
      className="px-[2.4rem] md:px-[4.8rem] tablet:pl-1 tablet:pr-[4.8rem] pt-[1.8rem] pb-[5.8rem] grow space-y-[4.2rem] max-h-full overflow-y-auto"
      ref={containerRef as React.RefObject<HTMLDivElement>}>
      {isOperationsDictionariesLoading ? (
        <HistoryFilterSkeleton />
      ) : (
        <div className="flex justify-between gap-[1.6rem] mb-[2rem]">
          <HistoryOperationTypeSelect
            onValueChange={handleSelectOperationType}
            value={filters.filter}
            options={operationTypes}
          />

          <div className="flex items-center gap-[1.6rem]">
            <HistoryDateFilterSelect
              filters={filters}
              onFiltersChange={setPageFilters}
              options={dateFilters}
              onResetPeriod={resetPeriod}
            />

            <HistoryExportButton onClick={handleHistoryExportClick} />
          </div>
        </div>
      )}

      {isOperationsHistoryLoading ? (
        <HistoryOperationsListSkeleton />
      ) : isOperationsListEmpty ? (
        <EmptyStateView
          isPeriodApplied={isPeriodApplied}
          onPeriodReset={resetPeriod}
        />
      ) : (
        <HistoryOperationsList
          data={filteredOperationsHistory}
          language={language}
          operationTypeIcons={operationTypeIcons}
          operationStatusIcons={operationStatusIcons}
          onOperationItemClick={handleOperationItemClick}
        />
      )}

      {hasScrolled && (
        <ScrollTopButton
          onClick={scrollToTop}
          className="absolute bottom-[3.4rem] inset-x-0 mx-auto"
        />
      )}
    </div>
  );
};

export { HistoryPage };
